import React from "react";
import { novelNextActionJourneyV1 } from "../utils/novelNextActionJourneyV1";
import { novelBookWorkspaceProgressV1 } from "../utils/novelBookWorkspaceProgressV1";

function clean(value) {
  return String(value ?? "").trim();
}

export default function NovelStoryWorkspaceNavigator({
  projectTitle = "",
  coverImageUrl = "",
  backCoverImageUrl = "",
  bookFormat = {},
  wordCount = 0,
  activeMatter = "manuscript",
  manuscriptWorkspace = "book-structure",
  frontMatterCount = 0,
  chapterCount = 0,
  sceneCount = 0,
  backMatterCount = 0,
  publicationStatus = "inDevelopment",
  journeyReadiness = {},
  reviewEnabled = true,
  proofEnabled = true,
  preparationEnabled = true,
  previewEnabled = false,
  finalApprovalEnabled = false,
  finalApprovalActive = false,
  onFinalApproval,
  previewBusy = false,
  previewActive = false,
  onPreview,
  onNavigate,
  onOpenWriting,
  onBack,
  isFR = false,
  pilotJourney = null,
  bookKind = "novel",
  physicalPageCount = 0,
  childrensBookUseNovelStagesV1E4D = false,
  nativeChildrensBookV1F2 = false,
  childrensBookAudienceReady = false,
  onChildrensBookBookSetup,
}) {
  // Children's Book reuses the actual Novel Book Workspace shell.
  // The original Novel path below is byte-for-byte unchanged when bookKind is novel.
  if (bookKind === "childrens-book" && !childrensBookUseNovelStagesV1E4D) {
    const stages = [
      ["book-structure", isFR ? "Structure du livre" : "Book Structure"],
      ["write", isFR ? "Écrire" : "Write"],
      ["review", isFR ? "Réviser" : "Review"],
      ["page-layout", isFR ? "Pages et illustrations" : "Pages & Illustrations"],
    ];
    const active = manuscriptWorkspace === "chapter" ? "write" : manuscriptWorkspace;
    return (
      <nav className="ns-textbook-studio-gateway ns-novel-story-book-gateway"
        aria-label={isFR ? "Espace livre jeunesse" : "Children's Book Workspace"}
        data-childrens-book-novel-parity-v1e="true">
        <div className="ns-textbook-studio-gateway-head">
          <div className="ns-textbook-studio-gateway-mark" aria-hidden="true"><span>NS</span></div>
          <div className="ns-textbook-studio-gateway-copy">
            <div className="ns-textbook-studio-gateway-kicker">
              {isFR ? "ESPACE LIVRE JEUNESSE" : "CHILDREN'S BOOK WORKSPACE"}
            </div>
            <div className="ns-textbook-studio-gateway-title">{clean(projectTitle) || (isFR ? "Votre livre" : "Your Book")}</div>
            <div className="ns-textbook-studio-gateway-note">
              {isFR ? "Pages liminaires · Manuscrit · Éléments de fin" : "Front Matter · Manuscript · Back Matter"}
            </div>
          </div>
          <div className="ns-textbook-studio-composition" aria-label={isFR ? "Composition du livre" : "Book composition"}>
            <span><strong>{frontMatterCount}</strong>{isFR ? " Avant" : " Front"}</span>
            <span><strong>{chapterCount}</strong>{isFR ? " Chap." : " Chapters"}</span>
            <span><strong>{backMatterCount}</strong>{isFR ? " Fin" : " Back"}</span>
            <span><strong>{physicalPageCount}</strong>{isFR ? " Pages" : " Pages"}</span>
          </div>
        </div>
        <div role="note" data-childrens-parity-proof-notice="true"
          style={{padding:"10px 12px",background:"rgba(255,255,255,0.12)",borderRadius:9,color:"#f0fff7"}}>
          {isFR ? "Revue en lecture seule. Aucun enregistrement ni publication." : "Read-only interface review. No saving or publishing."}
        </div>
        <div className="ns-textbook-studio-gateway-actions">
          {stages.map(([id,label])=>(
            <button key={id} type="button" data-childrens-parity-stage={id}
              className={"ns-textbook-studio-gateway-action "+(active===id?"is-active":"")}
              aria-current={active===id?"page":undefined}
              onClick={()=>onNavigate?.(id)}>
              <span className="ns-textbook-studio-gateway-action-label">{label}</span>
              <span className="ns-textbook-studio-gateway-action-status">{isFR ? "Voir" : "Open"}</span>
            </button>
          ))}
        </div>
        <div className="ns-novel-story-book-gateway-footer">
          <span>{isFR ? "L'écriture de scène suit l'expérience Roman / Histoire." : "Scene writing follows the Novel / Story Writer Studio experience."}</span>
          <button className="ns-button ns-button-outline" type="button" onClick={onBack}>
            {isFR ? "← Retour au Blueprint" : "← Back to Blueprint"}
          </button>
        </div>
      </nav>
    );
  }
  // The private PDF is a real workspace state, not a Book Structure fallback.
  const currentWorkspace =
    finalApprovalEnabled && finalApprovalActive ? "final-print-approval" :
    previewEnabled && previewActive
      ? "preview-book-interior"
      : activeMatter === "setup"
      ? "setup"
      : activeMatter === "manuscript"
      ? (
          manuscriptWorkspace === "publication-preparation"
            ? "publication-preparation"
            : manuscriptWorkspace === "publication-proof"
            ? "publication-proof"
            : manuscriptWorkspace === "review"
            ? "review"
            : manuscriptWorkspace === "write" ||
              manuscriptWorkspace === "chapter"
            ? "write"
            : manuscriptWorkspace === "overview"
            ? "overview"
            : "book-structure"
        )
      : "";

  const progress = novelBookWorkspaceProgressV1({
    bookFormat, frontCoverUrl: coverImageUrl, backCoverUrl: backCoverImageUrl,
    chapterCount, sceneCount, wordCount, publicationStatus, isFR,
  });

  const childrensAudienceGate = bookKind === "childrens-book" && childrensBookUseNovelStagesV1E4D;
  const stages = [
    {
      id: "setup",
      label: isFR ? "Préparer le livre" : "Book Setup",
      status: childrensAudienceGate
        ? (childrensBookAudienceReady ? (isFR ? "Public enregistré · prêt" : "Audience saved · ready")
          : (isFR ? "Public requis avant l’écriture" : "Audience required before writing"))
        : progress.setupStatus,
      disabled: false,
    },
    {
      id: "book-structure",
      label: isFR ? "Structure du livre" : "Book Structure",
      status: progress.structureStatus,
      disabled: false,
    },
    {
      id: "write",
      label: isFR ? "Écrire" : "Write",
      status: childrensAudienceGate && !childrensBookAudienceReady
        ? (isFR ? "Terminer Book Setup" : "Complete Book Setup") : progress.writeStatus,
      disabled: childrensAudienceGate && !childrensBookAudienceReady,
    },
    {
      id: "review",
      label: isFR ? "Réviser" : "Review",
      status: progress.reviewStatus,
      disabled: chapterCount < 1 || !reviewEnabled,
    },
    {
      id: "publication-preparation",
      label: isFR ? "Préparer la publication" : "Publication Preparation",
      status: progress.preparationStatus,
      disabled: chapterCount < 1 || !preparationEnabled,
    },
    {
      id: "publication-proof",
      label: isFR ? "Aperçu de mise en page" : "Book Layout Preview",
      status: progress.layoutStatus,
      disabled: chapterCount < 1 || !proofEnabled,
    },
    // All publication milestones remain visible as a roadmap. Visibility never
    // grants access: disabled controls preserve existing owner/pilot gates.
    {
      id: "preview-book-interior",
      label: isFR ? "Aperçu PDF de l’intérieur" : "Preview Interior PDF",
      status: !previewEnabled
        ? (isFR ? "Accès réservé · indisponible" : "Owner-only · unavailable")
        : previewBusy
        ? (isFR ? "Préparation…" : "Preparing…")
        : (isFR ? "Privé · provisoire" : "Private · provisional"),
      disabled: !previewEnabled || previewBusy,
    },
    {
      id: "final-print-approval",
      label: isFR ? "Approuver le PDF intérieur final" : "Approve Final Interior PDF",
      status: finalApprovalEnabled
        ? (isFR ? "Auteur · accès contrôlé" : "Creator · gated")
        : (isFR ? "Étape ultérieure · accès restreint" : "Later · restricted"),
      disabled: !finalApprovalEnabled,
    },
  ];

  // Read-only guidance; no visits, snapshots, previews, or publication approval
  // are inferred as completed. The editor retains control of manuscript status.
  const legacyNextAction = novelNextActionJourneyV1({
    publicationStatus, chapterCount, sceneCount, journeyReadiness,
    currentWorkspace, previewEnabled, previewBusy, finalApprovalEnabled,
  });
  // V2C2 only overrides guidance for the exact eligible owner pilot. A failed
  // owner-progress read must NOT silently fall back to 'Continue Writing'.
  const pilotActive = pilotJourney?.eligible === true;
  const pilotResult = pilotActive ? pilotJourney?.recommendation : null;
  const nextAction = childrensAudienceGate
    ? (childrensBookAudienceReady
      ? (Number(chapterCount) > 0 ? "write" : "book-structure") : "setup")
    : pilotActive
    ? (pilotResult?.ok === true ? pilotResult.next?.id : null)
    : (progress.suggestSetupFirst && legacyNextAction === "write"
      ? "setup" : legacyNextAction);
  const nextSteps = {
    "book-structure": {
      label: isFR ? "Organiser les chapitres et scènes" : "Organize Chapters & Scenes",
      detail: isFR ? "Ouvrir Structure du livre" : "Open Book Structure",
    },
    write: {
      label: Number(sceneCount) > 0
        ? (isFR ? "Continuer l’écriture" : "Continue Writing")
        : (isFR ? "Écrire la première scène" : "Write First Scene"),
      detail: Number(chapterCount) === 1 && typeof onOpenWriting === "function"
        ? (isFR ? "Ouvrir Writer Studio pour ce chapitre" : "Open Writer Studio for this Chapter")
        : Number(chapterCount) > 1
        ? (isFR ? "Choisir le chapitre à écrire" : "Choose a Chapter to write")
        : (isFR ? "Ouvrir Écrire" : "Open Write"),
    },
    review: {
      label: isFR ? "Relire le manuscrit" : "Review Your Manuscript",
      detail: isFR ? "Ouvrir Réviser" : "Open Review",
    },
    setup: {
      label: isFR ? "Vérifier Book Setup" : "Review Book Setup",
      detail: childrensAudienceGate
        ? (isFR ? "Enregistrez tranche d’âge, niveau et mode de lecture dans le Blueprint"
          : "Save Age Range, Reading Level and Reading Arrangement in the Blueprint")
        : progress.setupNextDetail,
    },
    "publication-preparation": {
      label: isFR ? "Préparer la publication" : "Publication Preparation",
      detail: isFR ? "Vérifier la préparation du livre" : "Check the saved-book readiness",
    },
    "publication-proof": {
      label: isFR ? "Vérifier la mise en page" : "Check Book Layout",
      detail: isFR ? "Ouvrir l’aperçu de mise en page" : "Open Book Layout Preview",
    },
    "preview-book-interior": {
      label: isFR ? "Prévisualiser le PDF intérieur" : "Preview Interior PDF",
      detail: isFR ? "Aperçu privé et provisoire" : "Private · provisional preview",
    },
    "final-print-approval": {
      label: isFR ? "Examiner l’approbation finale" : "Check Final Interior Approval",
      detail: isFR ? "Contrôles de l’auteur toujours obligatoires" : "Restricted approval checks still apply",
    },
  };
  const pilotUnavailable = pilotActive && pilotResult?.ok !== true;
  const pilotPublished = pilotActive && pilotResult?.next?.terminal === true;
  // Only an eligible pilot owner with an actually ready manuscript may jump
  // to the deliberate author checkpoint. Incomplete manuscripts stay at Write.
  const authorConfirmationNext = pilotActive &&
    pilotResult?.ok === true &&
    pilotResult?.next?.reason === "author-manuscript-confirmation-required" &&
    pilotResult?.next?.manuscriptConfirmationEligible === true;
  const reviewConfirmationNext = pilotActive &&
    pilotResult?.ok === true &&
    pilotResult?.next?.reason === "author-review-confirmation-required";
  const publicationInspectionNext = pilotActive &&
    pilotResult?.ok === true &&
    ["publication-readiness-inspection-required",
      "verified-publication-preparation-inspection-required"].includes(pilotResult?.next?.reason) &&
    pilotResult?.next?.advisoryOnly === true;
  const productionChecksNext = pilotActive &&
    pilotResult?.ok === true &&
    pilotResult?.next?.reason === "production-readiness-issues" &&
    pilotResult?.next?.id === "publication-preparation";
  const nextStep = pilotUnavailable
    ? { id: null, label: isFR ? "Progression non vérifiée" : "Verified progress unavailable",
        detail: isFR ? "Confirmation désactivée" : "Owner checkpoint service unavailable" }
    : pilotPublished
    ? { id: null, label: isFR ? "Livre déjà publié" : "Book already published",
        detail: isFR ? "Aucune modification demandée" : "No editing recommendation" }
    : { id: nextAction, ...nextSteps[nextAction],
        ...(authorConfirmationNext ? {
          label: isFR ? "Confirmer le manuscrit" : "Confirm manuscript readiness",
          detail: isFR ? "Utiliser la confirmation de l'auteur ci-dessous" : "Use the author confirmation below when ready",
        } : {}),
        ...(reviewConfirmationNext ? {
          label: isFR ? "Réviser puis confirmer" : "Review and confirm editorial completion",
          detail: isFR ? "Relire le manuscrit puis confirmer la révision ci-dessous" :
            "Read the saved book, then use the separate editorial confirmation below",
        } : {}),
        ...(publicationInspectionNext ? {
          label: isFR ? "Vérifier la préparation de publication" : "Inspect publication readiness",
          detail: pilotResult?.next?.formatVerified === true
            ? (isFR ? "Format enregistré vérifié ; préparation à inspecter, aucune approbation" :
              "Saved format verified; inspect preparation, no approval recorded")
            : (isFR ? "Contrôles du format enregistré, aucune approbation accordée" :
              "Check saved format and production readiness; no approval recorded"),
        } : {}),
        ...(productionChecksNext ? {
          label: isFR ? "Corriger la préparation de publication" : "Resolve publication readiness issues",
          detail: isFR ? "Ouvrir la liste des vérifications, sans approuver la publication" :
            "Open the existing checks; publication has not been approved",
        } : {}) };
  const nextStage = stages.find(stage => stage.id === nextStep.id);
  // The advisory engine keeps "write" as its milestone, but this particular
  // actionable recommendation must open the owner confirmation, not Write.
  const nextOpenDestination = authorConfirmationNext
    ? "author-confirmations" : nextStep.id;
  const canOpenSingleChapterStudio =
    Number(chapterCount) === 1 && typeof onOpenWriting === "function" &&
    (!childrensAudienceGate || (childrensBookAudienceReady && Number(sceneCount) > 0));

  function openSuggestedWriting() {
    if (canOpenSingleChapterStudio) {
      onOpenWriting();
      return;
    }
    onNavigate?.("write");
  }

  // V1F2F: Children’s Book displays the everyday journey first.
  // Every existing stage and its original authorization checks remains available.
  const primaryStagesV1F2F = nativeChildrensBookV1F2
    ? stages.filter((stage) => ["setup", "book-structure", "write", "review"].includes(stage.id))
    : stages;
  const advancedStagesV1F2F = nativeChildrensBookV1F2
    ? stages.filter((stage) => !["setup", "book-structure", "write", "review"].includes(stage.id))
    : [];
  const renderStageButtonV1F2F = (stage) => {
    const active = currentWorkspace === stage.id;
    const label = nativeChildrensBookV1F2 && stage.id === "book-structure"
      ? (isFR ? "Chapitres" : "Chapters")
      : stage.label;
    return (
      <button
        key={stage.id}
        type="button"
        data-novel-stage-id={stage.id}
        data-novel-preview-book-interior={stage.id === "preview-book-interior" ? "true" : undefined}
        disabled={stage.disabled || (childrensBookUseNovelStagesV1E4D &&
          !["setup","book-structure","write"].includes(stage.id))}
        aria-current={active ? "page" : undefined}
        className={"ns-textbook-studio-gateway-action " + (active ? "is-active" : "")}
        onClick={() => {
          if (stage.disabled || (childrensBookUseNovelStagesV1E4D &&
            !["setup","book-structure","write"].includes(stage.id))) return;
          if (childrensAudienceGate && stage.id === "setup") {
            onChildrensBookBookSetup?.();
            return;
          }
          if (stage.id === "final-print-approval") {
            if (typeof onFinalApproval === "function") onFinalApproval();
            return;
          }
          if (stage.id === "preview-book-interior") {
            if (typeof onPreview === "function") onPreview();
            return;
          }
          if (typeof onNavigate === "function") onNavigate(stage.id);
        }}
      >
        <span className="ns-textbook-studio-gateway-action-label">{label}</span>
        {(!nativeChildrensBookV1F2 || stage.disabled ||
          !["setup", "book-structure", "write", "review"].includes(stage.id)) ? (
          <span className="ns-textbook-studio-gateway-action-status">{stage.status}</span>
        ) : null}
      </button>
    );
  };

  return (
    <nav
      className="ns-textbook-studio-gateway ns-novel-story-book-gateway"
      data-novel-story-book-gateway="true"
      aria-label={
        nativeChildrensBookV1F2
          ? (isFR ? "Navigation de l’espace livre jeunesse" : "Children's Book Workspace navigation")
          : (isFR ? "Navigation de l’espace livre Roman / Histoire" : "Novel / Story Book Workspace navigation")
      }
    >
      <div className="ns-textbook-studio-gateway-head">
        <div
          className={
            "ns-textbook-studio-gateway-mark " +
            (clean(coverImageUrl)
              ? "ns-novel-story-gateway-cover"
              : "")
          }
          aria-hidden="true"
        >
          {clean(coverImageUrl) ? (
            <img
              src={clean(coverImageUrl)}
              alt=""
            />
          ) : (
            <span>NS</span>
          )}
        </div>

        <div className="ns-textbook-studio-gateway-copy">
          <div className="ns-textbook-studio-gateway-kicker">
            {isFR
              ? (nativeChildrensBookV1F2 || childrensBookUseNovelStagesV1E4D ? "ESPACE LIVRE JEUNESSE" : "ESPACE LIVRE ROMAN / HISTOIRE")
              : (nativeChildrensBookV1F2 || childrensBookUseNovelStagesV1E4D ? "CHILDREN'S BOOK WORKSPACE" : "NOVEL / STORY BOOK WORKSPACE")}
          </div>

          <div className="ns-textbook-studio-gateway-title">
            {clean(projectTitle) ||
              (isFR ? "Votre livre" : "Your Book")}
          </div>

          <div className="ns-textbook-studio-gateway-note">
            {isFR
              ? "Construisez le livre complet depuis un seul espace · Pages liminaires · Manuscrit · Éléments de fin"
              : "Build your complete book from one workspace · Front Matter · Manuscript · Back Matter"}
          </div>
        </div>

        <div
          className="ns-textbook-studio-composition"
          aria-label={isFR ? "Composition du livre" : "Book composition"}
        >
          <span>
            <strong>{Number(frontMatterCount) || 0}</strong>
            {isFR ? " Avant" : " Front"}
          </span>
          <span>
            <strong>{Number(chapterCount) || 0}</strong>
            {isFR ? " Chap." : " Chapters"}
          </span>
          <span>
            <strong>{Number(backMatterCount) || 0}</strong>
            {isFR ? " Fin" : " Back"}
          </span>
        </div>
      </div>

      <div
        data-novel-book-workspace-progress-v1="true"
        role="note"
        style={{ display: "flex", gap: 10, flexWrap: "wrap",
          padding: "7px 12px", fontSize: 12, lineHeight: 1.4,
          background: "rgba(255,255,255,0.12)", borderRadius: 9, color: "#f0fff7" }}
      >
        <strong>{childrensAudienceGate
          ? (childrensBookAudienceReady ? (isFR ? "Public enregistré" : "Audience saved")
            : (isFR ? "Configuration du public requise" : "Audience setup required"))
          : progress.summary}</strong>
        {!nativeChildrensBookV1F2 ? (
          <span>{childrensAudienceGate
            ? (childrensBookAudienceReady
                ? (isFR ? "L’écriture de scène est disponible." : "Scene writing is available.")
                : (isFR ? "La structure reste disponible ; enregistrez trois choix avant d’écrire."
                  : "Book Structure stays available; save the three audience choices before writing."))
            : progress.explanation}</span>
        ) : null}
      </div>

      <div
        data-novel-next-step-guidance-v1="true"
        role="note"
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 10, flexWrap: "wrap", padding: "9px 12px", borderRadius: 10,
          border: "1px solid #d1e5dd", background: "#f5fbf8" }}
      >
        <span style={{ fontSize: 13, lineHeight: 1.35, color: "#17352a" }}>
          <strong>{isFR ? "Étape suivante :" : "Next:"} {nextStep.label}</strong>
          {!nativeChildrensBookV1F2 ? (
            <span style={{ color: "#52635b", marginLeft: 8 }}>{nextStep.detail}</span>
          ) : null}
        </span>
        <button type="button" className="ns-button ns-button-outline"
          data-novel-next-step-action-v1={nextOpenDestination}
          disabled={(childrensBookUseNovelStagesV1E4D &&
            !["setup", "book-structure", "write"].includes(nextStep.id)) ||
            (!authorConfirmationNext && (!nextStage || nextStage.disabled))}
          onClick={() => {
            if (authorConfirmationNext) {
              // Scroll/focus only. Never submit or silently confirm a stage.
              const target = typeof document !== "undefined"
                ? document.querySelector('[data-novel-journey-checkpoint-v2c2="true"]')
                : null;
              target?.scrollIntoView?.({ behavior: "smooth", block: "start" });
              target?.querySelector?.('[data-novel-confirm-manuscript-v2c2="true"]')?.focus?.();
              return;
            }
            if (!nextStage || nextStage.disabled) return;
            if (childrensAudienceGate && nextStep.id === "setup") {
              onChildrensBookBookSetup?.();
              return;
            }
            if (nextStep.id === "final-print-approval") {
              if (typeof onFinalApproval === "function") onFinalApproval();
            } else if (nextStep.id === "preview-book-interior") {
              if (typeof onPreview === "function") onPreview();
            } else if (nextStep.id === "write") {
              openSuggestedWriting();
            } else if (typeof onNavigate === "function") {
              onNavigate(nextStep.id);
            }
          }}
          style={{ padding: "5px 10px", fontSize: 12 }}
        >
          {nextStep.id === "write" && !authorConfirmationNext
            ? canOpenSingleChapterStudio
              ? (isFR ? "Ouvrir Writer Studio →" : "Open Writer Studio →")
              : Number(chapterCount) > 1
                ? (isFR ? "Choisir un chapitre →" : "Choose Chapter →")
                : (isFR ? "Ouvrir →" : "Open →")
            : (isFR ? "Ouvrir →" : "Open →")}
        </button>
        {!childrensAudienceGate && !pilotActive && progress.suggestSetupFirst && nextStep.id === "setup" ? (
          <button type="button" className="ns-button ns-button-outline"
            data-novel-first-scene-alternative-v1="true"
            onClick={openSuggestedWriting}
            style={{ padding: "5px 10px", fontSize: 12 }}
          >
            {isFR ? "Ou écrire la première scène →" : "Or Write First Scene →"}
          </button>
        ) : null}
      </div>

      <div className="ns-textbook-studio-gateway-actions" data-native-childrens-primary-steps-v1f2f={nativeChildrensBookV1F2 ? "true" : undefined}>
        {primaryStagesV1F2F.map(renderStageButtonV1F2F)}
      </div>

      {nativeChildrensBookV1F2 && advancedStagesV1F2F.length > 0 ? (
        <details
          data-native-childrens-more-tools-v1f2f="true"
          defaultOpen={advancedStagesV1F2F.some((stage) => stage.id === currentWorkspace)}
          style={{ marginTop: 12, borderTop: "1px solid rgba(255,255,255,0.22)", paddingTop: 12 }}
        >
          <summary style={{ cursor: "pointer", fontWeight: 750, color: "#f5fff9", padding: "8px 2px" }}>
            {isFR ? "Autres outils · mise en page et publication" : "More tools · Layout and publication"}
          </summary>
          <div className="ns-textbook-studio-gateway-actions" style={{ marginTop: 12 }}>
            {advancedStagesV1F2F.map(renderStageButtonV1F2F)}
          </div>
        </details>
      ) : null}

      <div className="ns-novel-story-book-gateway-footer">
        <span>
          {nativeChildrensBookV1F2 ? "" : (isFR
            ? "Writer Studio reste l’espace concentré de la Scène active."
            : "Writer Studio remains the focused workspace for the active Scene.")}
        </span>

        <button
          className="ns-button ns-button-outline"
          type="button"
          onClick={onBack}
        >
          ← {isFR ? "Retour aux projets" : "Back to Projects"}
        </button>
      </div>
    </nav>
  );
}
